// =========================================================================
// `useFigureContext()` — the step context plus the handful of extras only the
// module and section pages read.
//
// `useWorkflowStepContext()` assembles everything the twenty-one workflow
// steps need. The module hub and the section landing print a different set of
// figures — the ones in `seed/modules.ts`'s `live`/`items` and
// `seed/areas.ts`'s `stat` — and those reach for six things no step does:
// the RMS fleet feed, the knowledge base, the customer's own EAM register,
// the tyre inspection log, the dispatch board and the service promises.
//
// Rather than widen the step context for three pages, they are added here and
// the two contexts compose. One extra domain is fetched (`rms`); the rest come
// out of domains the step context already loads.
//
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { useMemo } from 'react';
import { unpackTable } from '@/versions/v1/shared/columnar';
import {
  dispatchProposals,
  pmDue,
  promises,
} from '@/versions/v1/shared/oem-engine';
import { useOemData } from '@/versions/v1/shared/useOemData';
import type { CustomerSatisfactionResponse } from '@/versions/v1/schema/person';
import type { RosterEntry } from '@/versions/v1/schema/person';
import type { FigureContext } from './live-figure';
import { useWorkflowStepContext, type WorkflowContextState } from './workflow-context';

/** What {@link useFigureContext} returns. */
export interface FigureContextState extends Omit<WorkflowContextState, 'ctx'> {
  /** The composed context, or `null` until every domain has landed. */
  readonly figures: FigureContext | null;
  /** The step context inside it, for callers that only need that half. */
  readonly ctx: WorkflowContextState['ctx'];
}

/**
 * @param needsMaximo - forwarded to {@link useWorkflowStepContext}. The module
 *   pages that print `Object.keys(B.mx).length` or a warranty count pass
 *   `true`; the rest leave it off and those figures resolve to `0`.
 */
export function useFigureContext(needsMaximo = false): FigureContextState {
  const step = useWorkflowStepContext(needsMaximo);
  const rms = useOemData('rms');
  const people = useOemData('people');
  const service = useOemData('service');
  const telemetry = useOemData('telemetry');
  const costs = useOemData('costs');

  const loading = step.loading || rms.loading;
  const error = step.error ?? rms.error ?? null;

  const figures = useMemo((): FigureContext | null => {
    if (
      !step.ctx ||
      !step.raw ||
      !rms.data ||
      !people.data ||
      !service.data ||
      !telemetry.data ||
      !costs.data
    ) {
      return null;
    }

    const css = unpackTable<CustomerSatisfactionResponse>(people.data.css);
    const roster = unpackTable<RosterEntry>(people.data.roster);

    const dispatchCtx = {
      techJobs: service.data.tapp_jobs,
      workOrders: step.raw.workOrders,
      techs: people.data.tapp_techs,
      roster,
      css,
      timesheets: costs.data.timesheets,
      training: service.data.lms,
    };

    return {
      step: step.ctx,
      rmsStatus: rms.data.rms_status,
      rmsHealth: rms.data.rms_health,
      kb: service.data.kb,
      maximoAssets: service.data.maximo_assets,
      tyreRows: telemetry.data.tyres.rows,
      dispatchProposals: dispatchProposals(dispatchCtx),
      // `promises()` reads the Maximo register through `payFor`. With the
      // register absent it still returns every alert that has a service
      // window — the count is right, only the payer chip would be blank, and
      // nothing on these two pages prints the payer.
      promises: promises({ maximo: step.raw.maximo }),
      pmDue14: pmDue(14, { workOrders: step.raw.workOrders }),
      oil: telemetry.data.oil,
    };
  }, [step.ctx, step.raw, rms.data, people.data, service.data, telemetry.data, costs.data]);

  return {
    figures,
    ctx: step.ctx,
    raw: step.raw,
    loading,
    error,
    maximoPending: step.maximoPending,
  };
}
